import { useState, useRef, useEffect, useCallback } from 'react'
import {
    acquireCameraStream,
    stopCameraStream,
} from '../../WebAi/vision/cameraConfig'
import styles from './TrainMachine.module.css'
import renderImage from '../../../source/importImg'
import { connect } from 'react-redux'
import renderPrgImage from '../../../source/programImg'
import { useIndexedDB } from 'react-indexed-db'
import CaptureMarkers from './CaptureMarkers'

function CamModule(props) {
    const {
        selectedClass = '',
        isShow = false,
        imageUpload = false,
        handleDialogModalClose = () => {},
        // ImageClasses,
        currentModelId,
        camModels,
        setClasses,
        aiCodeMode = false,
        modelType = 'face',
    } = props
    const ImageClasses = camModels?.find(
        (model) => model?.modelId === currentModelId
    )?.ImageClasses
    const [startCam, setStartCam] = useState(false)
    const [stream, setStream] = useState(false)
    const [captureInterval, setCaptureInterval] = useState(null)
    const [imageTypeError, setImageTypeError] = useState(false)
    const [isDragging, setIsDragging] = useState(false)
    const [capturedSamples, setCapturedSamples] = useState([])
    const [bursting, setBursting] = useState(false)
    const { add, getAll, deleteRecord } = useIndexedDB('Samples')
    const BURST_COUNT = 10
    const BURST_INTERVAL = 150

    const videoRef = useRef()
    const imagesEndRef = useRef()
    const imageInputRef = useRef(null)
    const errorTimeOutRef = useRef(null)

    const loadSamples = useCallback(async () => {
        try {
            if (!currentModelId || !selectedClass) return
            const allSamples = await getAll()
            const filtered = allSamples.filter(
                (s) =>
                    s.modelId === currentModelId &&
                    s.classId === selectedClass &&
                    s.sampleType === 'Image'
            )
            setCapturedSamples(filtered)
        } catch (error) {
            console.error('Error loading samples:', error)
        }
    }, [getAll, currentModelId, selectedClass])

    useEffect(() => {
        if (isShow && currentModelId && selectedClass) loadSamples()
    }, [isShow, currentModelId, selectedClass, loadSamples])

    useEffect(() => {
        // Scroll only the captured-images container to its end. Using
        // scrollIntoView here scrolled the whole page (header out of view) when
        // the container wasn't the scroll parent.
        const container = imagesEndRef.current?.parentElement
        if (container) container.scrollTop = container.scrollHeight
    }, [capturedSamples])

    //start the web cam
    const startWebcam = async () => {
        // Use the exact camera chosen in Configure > Inputs (aiCameraDeviceId)
        // so training captures from the same device, not the default one. A bare
        // deviceId is only advisory, so we force it with { exact } and fall back
        // to any camera only if that device is genuinely unavailable.
        const deviceId = sessionStorage.getItem('aiCameraDeviceId')
        const open = (constraint) =>
            navigator.mediaDevices.getUserMedia({
                video: constraint,
                audio: false,
            })
        try {
            let stream
            if (aiCodeMode) {
                // AI Studio: honour the chosen source (a paired phone camera or
                // the selected local webcam) via the shared acquirer.
                stream = await acquireCameraStream(deviceId || undefined)
            } else {
                stream = await open(true)
            }
            setStream(true)
            videoRef.current.srcObject = stream
            videoRef.current.play()
            setStartCam(true)
        } catch (err) {
            console.error('Webcam error:', err)
            alert('Please connect your the camera.')
        }
    }

    //stop the web cam
    const stopWebcam = () => {
        try {
            // Guarded: never stops a paired phone stream (the WebRTC peer owns
            // it); a normal local webcam is stopped as before.
            stopCameraStream(videoRef.current?.srcObject)
            setStream(false)
        } catch (e) {
            console.error('ERROR', e)
        }
    }

    const captureImageFromVideo = () => {
        const canvas = document.getElementById('canvas')
        const context = canvas.getContext('2d')
        context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height)
        return canvas
    }

    const handleCaptureFromVideo = async () => {
        const canvas = captureImageFromVideo()
        await saveCapturedImage(canvas.toDataURL('image/jpeg'))
    }

    //check it is a image file or not
    const isImageFile = (file) => {
        return (
            file &&
            (file.type.startsWith('image/') ||
                ['.jpg', '.jpeg', '.png', '.webp'].some((ext) =>
                    file.name.toLowerCase().endsWith(ext)
                ))
        )
    }

    const processUploadImage = (file) => {
        if (file) {
            if (errorTimeOutRef.current) {
                clearTimeout(errorTimeOutRef.current)
                errorTimeOutRef.current = null
                setImageTypeError(false)
            }
            const reader = new FileReader()
            reader.onloadend = () => {
                saveCapturedImage(reader.result)
            }

            reader.readAsDataURL(file) // Convert the file to base64
        }
    }

    const processUploadImages = (files) => {
        const list = Array.from(files || [])
        const validFiles = list.filter((file) => isImageFile(file))
        const hasInvalid = list.length !== validFiles.length

        validFiles.forEach((file) => processUploadImage(file))

        if (hasInvalid) {
            setImageTypeError(true)
            if (errorTimeOutRef.current) {
                clearTimeout(errorTimeOutRef.current)
                errorTimeOutRef.current = null
            }
            errorTimeOutRef.current = setTimeout(() => {
                setImageTypeError(false)
                errorTimeOutRef.current = null
            }, 3000)
        }
    }

    const handleImageUpload = (event) => {
        processUploadImages(event.target.files)
        event.target.value = ''
    }

    // Start continuous capture
    const startContinuousCapture = () => {
        if (!captureInterval) {
            handleCaptureFromVideo()
            const interval = setInterval(() => {
                handleCaptureFromVideo()
            }, 200) // Capture every 200ms
            setCaptureInterval(interval)
        }
    }

    // Stop continuous capture
    const stopContinuousCapture = () => {
        if (captureInterval) {
            clearInterval(captureInterval)
            setCaptureInterval(null)
        }
    }

    // One-tap burst: automatically grab a fixed number of samples, paced so the
    // frames differ slightly (matches the POC's "Burst" capture).
    const burstCapture = async () => {
        if (bursting || !startCam) return
        setBursting(true)
        try {
            for (let i = 0; i < BURST_COUNT; i++) {
                await handleCaptureFromVideo()
                await new Promise((r) => setTimeout(r, BURST_INTERVAL))
            }
        } catch (e) {
            console.error('Burst capture error:', e)
        } finally {
            setBursting(false)
        }
    }

    //image save to IndexedDB
    const saveCapturedImage = async (imageSrc) => {
        const sample = {
            modelId: currentModelId,
            classId: selectedClass,
            timestamp: Date.now(),
            sampleType: 'Image',
            sampleData: imageSrc, // Store Base64 in IndexedDB
        }

        const id = await add(sample)
        // Functional update so rapid captures (burst / hold) accumulate instead
        // of overwriting each other via a stale closure.
        let newLen = 0
        setCapturedSamples((prev) => {
            const updated = [...prev, { ...sample, id }]
            newLen = updated.length
            return updated
        })

        // Update Redux metadata only
        const selectedClassDetails = { ...ImageClasses[selectedClass] }
        selectedClassDetails.capturedImagesCount = newLen

        // Clear redundant Base64 from Redux if it was previously there
        if (selectedClassDetails.capturedImages) {
            delete selectedClassDetails.capturedImages
        }

        setClasses({
            key: selectedClass,
            data: selectedClassDetails,
            classType: 'ImageClasses',
        })
    }

    const handleDrop = (event) => {
        event.preventDefault()
        setIsDragging(false)
        processUploadImages(event.dataTransfer.files)
    }
    const handleDragOver = (event) => {
        event.preventDefault()
        setIsDragging(true)
    }

    const handleDragLeave = () => {
        setIsDragging(false)
    }

    //remove only single image
    const removeSingleSample = async (id) => {
        try {
            await deleteRecord(id)
            const updatedSamples = capturedSamples.filter((s) => s.id !== id)
            setCapturedSamples(updatedSamples)

            const selectedClassDetails = { ...ImageClasses[selectedClass] }
            selectedClassDetails.capturedImagesCount = updatedSamples.length

            setClasses({
                key: selectedClass,
                data: selectedClassDetails,
                classType: 'ImageClasses',
            })
        } catch (e) {
            console.error('ERROR', e)
        }
    }

    useEffect(() => {
        if (isShow && !imageUpload) {
            startWebcam()
        }
    }, [isShow, imageUpload])

    return (
        <>
            {isShow && (
                <div className={styles.webCamModel}>
                    <div className={styles.webCamModelInner}>
                        {/* --------- close button --------------- */}
                        <img
                            className={styles.WebCamModelCloseBtn}
                            src={renderImage('clos')}
                            onClick={() => {
                                stopWebcam()
                                setStartCam(false)
                                // clsoeSave()
                                handleDialogModalClose()
                            }}
                            alt="Close"
                        />

                        {imageUpload ? (
                            <div
                                className={`${styles.webCamModelUploadDiv} ${
                                    isDragging
                                        ? styles.webCamModelUploadDivDragging
                                        : ''
                                }`}
                                onClick={() =>
                                    imageInputRef?.current &&
                                    imageInputRef?.current?.click()
                                }
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                            >
                                <input
                                    ref={imageInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    hidden
                                    onChange={handleImageUpload}
                                />
                                <span className={styles.webCamModelUploadIcon}>
                                    <svg
                                        width="26"
                                        height="26"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="17 8 12 3 7 8" />
                                        <line x1="12" y1="3" x2="12" y2="15" />
                                    </svg>
                                </span>
                                <p className={styles.webCamModelUploadText}>
                                    Choose images from your files, or drag &
                                    drop here
                                </p>

                                {imageTypeError && (
                                    <p
                                        className={`${styles.webCamModelUploadText} ${styles.webCamModelUploadTextError}`}
                                    >
                                        Only image files are allowed. Supported
                                        formats: .jpg, .jpeg, .png, .webp
                                    </p>
                                )}
                                <canvas
                                    id="canvas"
                                    width="224"
                                    height="224"
                                    style={{ display: 'none' }}
                                ></canvas>
                            </div>
                        ) : (
                            <div>
                                <div className={styles.webCamModelCameraDiv}>
                                    {stream ? (
                                        <div
                                            style={{
                                                position: 'relative',
                                                width: 250,
                                                height: 250,
                                            }}
                                        >
                                            <video
                                                ref={videoRef}
                                                height={250}
                                                width={250}
                                                style={{ borderRadius: '10px' }}
                                            />
                                            {/* Live training markers (face box
                                                / pose skeleton) for AI-code. */}
                                            {aiCodeMode && (
                                                <CaptureMarkers
                                                    videoRef={videoRef}
                                                    kind={modelType}
                                                    active={stream}
                                                />
                                            )}
                                        </div>
                                    ) : (
                                        <div
                                            style={{
                                                height: '250px',
                                                width: '250px',
                                            }}
                                        >
                                            Webcam is off
                                        </div>
                                    )}
                                    <canvas
                                        id="canvas"
                                        width="224"
                                        height="224"
                                        style={{ display: 'none' }}
                                    ></canvas>
                                </div>
                                <div className={styles.webCamModelBtnDiv}>
                                    <button
                                        disabled={!startCam || bursting}
                                        // onClick={handleCaptureFromVideo}
                                        onMouseDown={startContinuousCapture}
                                        onMouseUp={stopContinuousCapture}
                                        onMouseLeave={stopContinuousCapture}
                                        className={styles.capturePhotoBtn}
                                        style={
                                            !startCam || bursting
                                                ? { opacity: '0.5' }
                                                : {}
                                        }
                                    >
                                        Capture Photo
                                    </button>
                                    <button
                                        disabled={!startCam || bursting}
                                        onClick={burstCapture}
                                        className={styles.capturePhotoBtn}
                                        style={
                                            !startCam || bursting
                                                ? { opacity: '0.5' }
                                                : {}
                                        }
                                    >
                                        {bursting
                                            ? 'Capturing…'
                                            : `Burst (${BURST_COUNT})`}
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className={styles.samplesWrapper}>
                            {capturedSamples?.length > 0 && (
                                <div className={styles.imageCountDiv}>
                                    <p style={{ marginBottom: '0' }}>
                                        {capturedSamples?.length}{' '}
                                        Image samples
                                    </p>
                                </div>
                            )}

                            {/* --------- images ----------------------------- */}
                            <div className={styles.captureImagesDiv}>
                                {capturedSamples.map((s) => (
                                    <div
                                        key={s.id}
                                        className={styles.sampleImgDiv}
                                    >
                                        <img
                                            width="70px"
                                            src={s.sampleData}
                                            alt={`Captured ${s.id}`}
                                            style={{ borderRadius: '5px' }}
                                        />
                                        <img
                                            src={renderPrgImage(
                                                'eachImgCross'
                                            )}
                                            className={styles.imgDelete}
                                            id={styles.imgDelete}
                                            alt="delete each image"
                                            onClick={() =>
                                                removeSingleSample(s.id)
                                            }
                                        />
                                        <div
                                            className={`${styles.tooltipDiv} ${styles.imgDeleteToolTip}`}
                                        >
                                            Delete image
                                        </div>
                                    </div>
                                ))}
                                <div ref={imagesEndRef}></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

const mapStateToProps = (state) => {
    return {
        appModeStorage: state.appModeStorage,
        // ImageClasses: state.appModeStorage.trainMachine.ImageClasses,
        camModels: state.appModeStorage.trainMachine.camModels,
        currentModelId: state.appModeStorage.trainMachine.currentModelId,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        //AI_TRAIN_LIST
        setClasses: (data) => {
            dispatch({ type: 'ADD_CLASSES', payload: data })
        },
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(CamModule)
